/* eslint-disable no-unused-vars */
import { useRef, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { formatCi, formatFullNumber } from "../../utils";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Paginator } from "primereact/paginator";

import { CaseStatus, UserRole } from "../../constants";
import Dashboard from "../../Layout/Dashboard";
import {
  useClients,
  useGetInsurance,
  useStaleMedicalCases,
  usePendingRequests,
} from "../../hooks/queries";
import { Calendar } from "primereact/calendar";
import { FloatLabel } from "primereact/floatlabel";
import { PaginatorTemplate } from "../../Layout/Paginator/PaginatorTemplate";
import ExportCasesDialog from "../Modals/ExportCasesDialog";
import { useEffect } from "react";

/**
 * Medical cases table
 */
export default function PendingRequestList() {
  const role = localStorage.getItem("role");

  const [pageSize, setPageSize] = useState(20);
  const [lazyFilters, setLazyFilters] = useState({
    status: CaseStatus.REQUESTED,
  });
  const [lazyState, setLazyState] = useState({
    first: 1,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {},
  });

  // Get medical cases
  const casesQuery = usePendingRequests({
    page: lazyState.page,
    pageSize,
    filters: lazyFilters,
  });

  const staleCases = useStaleMedicalCases({ page: 1, filters: {} });

  const navigate = useNavigate();

  // Case detail link
  const ViewDetailBodyTemplate = (row) => (
    <Link to={`/casos/${row.id}`} className="p-button  text-sm p-2">
      <span className="p-button-icon p-button-icon-left pi pi-eye"></span>
      <span className="p-button-label">Ver</span>
    </Link>
  );

  // Status change
  const [newStatusMultiple, setNewStatusMultiple] = useState(null);
  const [showMultipleModal, setShowMultipleModal] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);

  const toastRef = useRef(null);

  // Table multiple selection
  const [selectedRows, setSelectedRows] = useState(null);

  const clientsQuery = useClients(false);

  const useInsurance = useGetInsurance();
  const insurance = useInsurance?.data;

  const [showPendingDialog, setShowPendingDialog] = useState(null);

  // Global filters
  const [filters] = useState({
    // global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    company_client: { value: null, matchMode: FilterMatchMode.CONTAINS },
    created: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: {
      value: CaseStatus.REQUESTED,
      matchMode: FilterMatchMode.CONTAINS,
    },
    "patient_.sex": { value: null, matchMode: FilterMatchMode.EQUALS },
    requirement_type_names: { value: null, matchMode: FilterMatchMode.EQUALS },
    "holder_.ci": { value: null, matchMode: FilterMatchMode.CONTAINS },
    "patient_.ci": { value: null, matchMode: FilterMatchMode.CONTAINS },
    "patient_.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
    "patient_.birthdate": { value: null, matchMode: FilterMatchMode.CONTAINS },
    subsidiary_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "patient_.phone": { value: null, matchMode: FilterMatchMode.CONTAINS },
    state_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const clientRowFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={clientsQuery.data ?? []}
        optionLabel="name"
        optionValue="id"
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        placeholder="Seleccionar cliente"
        className="p-column-filter min-w-min"
        showClear
      />
    );
  };

  /**
   * Page event
   *
   */
  const onPageChange = (e) => {
    setPageSize(e.rows);
    setLazyState({ ...lazyState, first: e.first, page: e.page + 1 });
  };

  const onFilter = (event) => {
    event["first"] = 0;
    setLazyState(event);
    let status = event.filters.status.value;
    let prioritized = null;
    if (status === CaseStatus.PRIORITIZED) {
      status = null;
      prioritized = true;
    }

    setLazyFilters({
      ...lazyFilters,
      subsidiary__company_client: event.filters.company_client.value,
      company_client: event.filters.company_client.value,
      status: status,
      prioritized: prioritized,
      id_parent: event.filters.id.value,
      // id__contains: event.filters.id.value,
      holder__ci__icontains: event.filters["holder_.ci"].value,
      patient__ci__icontains: event.filters["patient_.ci"].value,
      patient__name__icontains: event.filters["patient_.name"].value,
      state__name__icontains: event.filters.state_name.value,
      parent_case__id__icontains: event.filters.id.value,
    });
  };
  const [showExportDialog, setShowExportDialog] = useState(false);

  useEffect(() => {
    if (staleCases.data?.count > 0 && role !== UserRole.SUB_ADMINISTRATOR) {
      const currentTime = Date.now();
      const storedTime =
        localStorage.getItem("lastNavigationTime") ||
        currentTime - 30 * 60 * 1000;
      // 30 * 60 *
      if (!storedTime) {
        localStorage.setItem("lastNavigationTime", currentTime);
        setShowPendingDialog(true);
      }

      if (storedTime && currentTime - storedTime >= 60 * 1000) {
        localStorage.setItem("lastNavigationTime", currentTime);
        // navigate("/casos-pendientes");
        setShowPendingDialog(true);
      }
    }
  }, [staleCases.data?.count, role, navigate]);

  const handleExportClick = () => {
    setShowExportDialog(true);
  };

  const handleExportDialogHide = () => {
    setShowExportDialog(false);
  };

  /**
   * Cases table header
   */
  const TableHeader = ({ newStatus, changeNewStatus, onStatusChangeClick }) => {
    return (
      <div className="full-w-header">
        <div className=" mb-2">
          <FloatLabel>
            <Calendar
              locale="es"
              inputId="created_date"
              value={dateFilter}
              onChange={(e) => {
                if (
                  e.originalEvent.type === "click" ||
                  e.originalEvent.type === "keypress"
                ) {
                  setDateFilter(e.value);

                  const dateFormat = e.value?.toISOString().split("T")[0];
                  setLazyFilters({
                    ...lazyFilters,
                    created_at__date: dateFormat,
                  });
                }
                if (e.value === dateFilter) return;

                if (!e.value) {
                  setDateFilter(null);
                }
              }}
              dateFormat="mm/dd/yy"
              placeholder="dia/mes/año"
              mask="99/99/9999"
              showButtonBar
            />
            <label htmlFor="created_date">Buscar por fecha</label>
          </FloatLabel>
        </div>
        <div>
          <Button
            label="Exportar"
            icon="pi pi-file-pdf"
            severity="warning"
            className="p-button-success"
            onClick={handleExportClick}
          />
        </div>
      </div>
    );
  };

  console.log(casesQuery.data);

  return (
    <Dashboard headerTitle="Solicitudes pendientes">
      <div className="card">
        <Toast ref={toastRef} position="top-center" />
        <DataTable
          value={casesQuery.data?.results ?? []}
          dataKey="id"
          size={"small"}
          filters={filters}
          loading={casesQuery.isLoading}
          onFilter={onFilter}
          header={
            <TableHeader
              newStatus={newStatusMultiple}
              changeNewStatus={setNewStatusMultiple}
              onStatusChangeClick={() => setShowMultipleModal(true)}
            />
          }
          emptyMessage="No se hallaron casos."
          selectionMode="checkbox"
          selection={selectedRows}
          onSelectionChange={(e) => setSelectedRows(e.value)}
          stripedRows
          scrollable
          removableSort
        >
          <Column
            field="company_client"
            header="Cliente"
            filter
            filterPlaceholder="Buscar por Cliente"
            filterElement={clientRowFilterTemplate}
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            body={(row) => {
              if (role === UserRole.MEDICAL_CENTER) {
                return row.parent_medical_center_name ?? row.company_client;
              }
              return row.company_client;
            }}
          />
          <Column
            field="created"
            header="Fecha"
            // filter
            dataType="date"
            filterPlaceholder="Buscar por fecha"
            // showFilterMatchModes={false}
            // showClearButton={false}
            // showApplyButton={false}
            // filterElement={dateFilterTemplate}
          />

          <Column
            field="id"
            header="ID #"
            sortable
            filter
            filterPlaceholder="Buscar por Nro. de Caso"
            body={(row) => {
              return row.parent_case_id ?? row.id;
            }}
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
          />
          <Column
            field="holder_.ci"
            header="CI Titular"
            filter
            filterPlaceholder="Buscar por Titular"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
            body={(row) => formatCi(row.holder_.ci)}
          />
          <Column
            field="patient_.ci"
            header="CI Paciente"
            filter
            filterPlaceholder="Buscar por CI Paciente"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
            body={(row) => formatCi(row.patient_.ci)}
          />
          <Column
            field="patient_.name"
            header="Nombre Paciente"
            filter
            filterPlaceholder="Buscar por Nombre"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
          />
          {/* <Column
            field="subsidiary_name"
            header="Filial"
            filter
            filterPlaceholder="Buscar por Filial"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
          /> */}
          <Column
            field="patient_.phone"
            header="Teléfono Paciente"
            filter
            filterPlaceholder="Buscar por Teléfono"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
            body={(row) => formatFullNumber(row.patient_.phone)}
          />
          {insurance?.name === "Vértice Seguros" && (
            <Column
              field="state_name"
              header="Estado"
              filter
              filterPlaceholder="Buscar por Estado"
              showFilterMatchModes={false}
              showClearButton={false}
              showApplyButton={false}
              bodyClassName="readable-cell"
              sortable
            />
          )}
          <Column
            header="Acción"
            body={ViewDetailBodyTemplate}
            frozen
            alignFrozen="right"
          />
        </DataTable>
        <Paginator
          first={lazyState.first}
          rows={pageSize}
          totalRecords={casesQuery.data?.count}
          template={PaginatorTemplate}
          // rowsPerPageOptions={[10, 20, 30, 50,100]}
          onPageChange={onPageChange}
        />
        <ExportCasesDialog
          visible={showExportDialog}
          onHide={handleExportDialogHide}
          initialStatus={[CaseStatus.REQUESTED]}
        />
      </div>
    </Dashboard>
  );
}
